/**
 * Skill Details Page - Comprehensive view of a specific skill
 * 
 * This component provides a detailed breakdown of individual skills by displaying:
 * - Company logos and statistics where the skill was used
 * - Chronological timeline of skill development across activities
 * - Related projects that utilized the skill
 * - Experience summary and proficiency metrics
 * 
 * Features:
 * - Dynamic routing with URL encoding for skill names (handles special characters like C#)
 * - Tab-based navigation between three views: overview, timeline, and projects
 * - Responsive design with mobile-optimized layouts
 * - Breadcrumb navigation back to professional skills page
 * - Animated transitions and loading states
 * - Error handling with fallback to NotFound page
 * 
 * Route: /professional/skill/:skillName
 * 
 * @param skillName - URL-encoded skill name from route parameters
 * @returns JSX element containing the skill details page or NotFound component
 * 
 * @example
 * // URLs handled by this component:
 * // /professional/skill/javascript -> Shows JavaScript skill details
 * // /professional/skill/c- -> Shows C# skill details (URL encoded)
 * // /professional/skill/machine-learning -> Shows Machine Learning skill details
 */

import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useRoute, useLocation, Link } from 'wouter';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SkillIcon } from '@/components/skills/ui/SkillIcon';
import { SkillPointsRatio } from '@/components/skills/ui/SkillPointsRatio';
import { UnifiedProjectsComponent } from '@/components/UnifiedProjects';
import { 
  ArrowLeft, 
  Building2, 
  Calendar, 
  Clock, 
  TrendingUp, 
  Target, 
  Activity,
  Briefcase,
  Users,
  BarChart3,
  Award,
  Zap
} from 'lucide-react';
import { 
  getSkillDetails, 
  decodeSkillName, 
  SkillDetails as SkillDetailsType,
  SkillProject,
  SkillCompany,
  SkillTimeline,
  isSkillCertified,
  getSkillCertifications
} from '@/lib/skills';
import { activities } from '../../../db';
import { calculateProfessionalSkills } from '@/lib/skills';
import { createActivitySlug } from '@/lib/activityUtils';
import { createCompanySlug } from '@/lib/companyUtils';
import { getSkillCategoryStyles, getCategoryDisplayName } from '@/lib/skills';
import { CERTIFICATION_DESIGN } from '@/lib/utils';
import { 
  ANIMATION_TRANSITIONS, 
  ANIMATION_DURATION, 
  createStaggeredDelay 
} from '@/lib/animationConstants';
import NotFound from './not-found';

/**
 * Main Skill Details Page Component
 * 
 * Renders a comprehensive view of an individual skill including:
 * - Header with skill name, category badge, and current usage status
 * - Statistics overview cards (companies, activities, projects, proficiency)
 * - Experience summary with total duration and usage timeline
 * - Tab navigation for different views (overview, timeline, projects)
 * - Responsive layout with consistent animations
 * 
 * State Management:
 * - activeTab: Controls which tab content is displayed
 * - skillName: Decoded skill name from URL parameters
 * - skillDetails: Memoized comprehensive skill data
 * 
 * Performance Optimizations:
 * - Memoized skill name decoding to prevent unnecessary recalculations
 * - Memoized skill details fetching with caching in skillUtils
 * - Conditional rendering to avoid expensive operations for invalid skills
 * 
 * @returns JSX.Element - The complete skill details page or NotFound component
 */
export default function SkillDetailsPage() {
  const [, params] = useRoute('/professional/skill/:skillName');
  const [location, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'projects' | 'certificates'>('overview');

  const skillName = useMemo(() => {
    return params?.skillName ? decodeSkillName(params.skillName as string) : '';
  }, [params?.skillName]);

  const skillDetails = useMemo(() => {
    return skillName ? getSkillDetails(skillName) : null;
  }, [skillName]);

  const professionalSkills = useMemo(() => {
    return calculateProfessionalSkills();
  }, []);

  if (!skillDetails) {
    return <NotFound />;
  }

  const formatDuration = (months: number): string => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) {
      return `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    } else if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      return `${years}y ${remainingMonths}m`;
    }
  };

  const formatDate = (date: string | null): string => {
    if (!date || date === 'Present') return 'Present';
    
    const parsedDate = new Date(date);
    return parsedDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  return (
    <TooltipProvider>
      <main className="container mx-auto p-6 min-h-screen">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Breadcrumb Navigation */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-2 text-sm text-muted-foreground"
          >
            <Link href="/professional">
              <Button variant="ghost" size="sm" className="gap-2 p-2">
                <ArrowLeft className="h-4 w-4" />
                Professional Skills
              </Button>
            </Link>
            <span>/</span>
            <span className="text-foreground font-medium">{skillDetails.name}</span>
          </motion.div>

          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={ANIMATION_TRANSITIONS.SECTION_1}
            className="text-center space-y-4"
          >
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-primary/10 to-transparent rounded-lg blur-lg"></div>
              <div className="flex items-center justify-center gap-4 relative z-10 px-6 py-3">
                <SkillIcon 
                  skillName={skillDetails.name} 
                  size="xl" 
                  showFallback={false}
                  className="text-3xl md:text-4xl"
                />
                <h1 className="text-4xl md:text-5xl font-bold">
                  {skillDetails.name}
                </h1>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Badge 
                variant="outline" 
                className={`text-sm ${getSkillCategoryStyles(skillDetails.category)}`}
              >
                {getCategoryDisplayName(skillDetails.category)}
              </Badge>
              {skillDetails.isCurrentlyUsed && (
                <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                  Currently Active
                </Badge>
              )}
            </div>
          </motion.div>

          {/* Statistics Overview Cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={ANIMATION_TRANSITIONS.SECTION_2}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
          >
            <Card className="text-center">
              <CardContent className="pt-6">
                <Building2 className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                <div className="text-2xl font-bold">{skillDetails.totalCompanies}</div>
                <div className="text-sm text-muted-foreground">
                  {skillDetails.totalCompanies === 1 ? 'Company' : 'Companies'}
                </div>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <Activity className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <div className="text-2xl font-bold">{skillDetails.totalActivities}</div>
                <div className="text-sm text-muted-foreground">
                  {skillDetails.totalActivities === 1 ? 'Role' : 'Roles'}
                </div>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <Briefcase className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                <div className="text-2xl font-bold">{skillDetails.totalProjects}</div>
                <div className="text-sm text-muted-foreground">
                  {skillDetails.totalProjects === 1 ? 'Project' : 'Projects'}
                </div>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <Zap className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                <SkillPointsRatio 
                  skillSpecificPoints={skillDetails.skillSpecificPoints}
                  totalSkillPoints={skillDetails.totalSkillPoints}
                  skillName={skillDetails.name}
                  category={skillDetails.category}
                  calculationType="professional"
                  skillsByCategory={professionalSkills}
                  className="justify-center"
                />
                <div className="text-sm text-muted-foreground">
                  Skill Points
                </div>
              </CardContent>
            </Card>

            {/* Certification Card */}
            <Card className="text-center">
              <CardContent className="pt-6">
                <Award className={`h-8 w-8 mx-auto mb-2 ${CERTIFICATION_DESIGN.colors.badge}`} />
                <div className="text-2xl font-bold">
                  {isSkillCertified(skillDetails.name) ? getSkillCertifications(skillDetails.name).length : 0}
                </div>
                <div className="text-sm text-muted-foreground">
                  {getSkillCertifications(skillDetails.name).length === 1 ? 'Certificate' : 'Certificates'}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Experience Summary */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={ANIMATION_TRANSITIONS.SECTION_3}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Experience Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {formatDuration(Math.round(skillDetails.totalExperience))}
                    </div>
                    <div className="text-sm text-muted-foreground">Total Experience</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {formatDate(skillDetails.firstUsed)}
                    </div>
                    <div className="text-sm text-muted-foreground">First Used</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {formatDate(skillDetails.lastUsed)}
                    </div>
                    <div className="text-sm text-muted-foreground">Last Used</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Tab Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={ANIMATION_TRANSITIONS.SECTION_4}
            className="flex justify-center"
          >
            <div className="flex bg-muted rounded-lg p-1">
              <Button
                variant={activeTab === 'overview' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('overview')}
                className="gap-2"
              >
                <Building2 className="h-4 w-4" />
                Companies
              </Button>
              <Button
                variant={activeTab === 'timeline' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('timeline')}
                className="gap-2"
              >
                <Calendar className="h-4 w-4" />
                Timeline
              </Button>
              <Button
                variant={activeTab === 'projects' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('projects')}
                className="gap-2"
              >
                <Briefcase className="h-4 w-4" />
                Projects
              </Button>
              {/* Show certificates tab only if skill has certifications */}
              {isSkillCertified(skillDetails.name) && (
                <Button
                  variant={activeTab === 'certificates' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('certificates')}
                  className="gap-2"
                >
                  <Award className="h-4 w-4" />
                  Certificates
                </Button>
              )}
            </div>
          </motion.div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={ANIMATION_TRANSITIONS.TAB_CHANGE}
          >
            {activeTab === 'overview' && (
              <CompaniesOverview 
                companies={skillDetails.companies} 
                skillDetails={skillDetails}
                professionalSkills={professionalSkills}
                activities={activities}
              />
            )}
            {activeTab === 'timeline' && (
              <SkillTimelineView 
                timeline={skillDetails.timeline} 
                skillDetails={skillDetails}
                professionalSkills={professionalSkills}
                activities={activities}
              />
            )}
            {activeTab === 'projects' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold">Projects using this skill</h3>
                  <UnifiedProjectsComponent
                    showPrivateProjects={true}
                    defaultViewMode="list"
                    showCompanyLogos={true}
                    requiredSkills={[skillDetails.name]}
                    emptyStateMessage={`No projects found using ${skillDetails.name}. This might be due to different skill categorization.`}
                  />
                </div>
              </motion.div>
            )}
            {activeTab === 'certificates' && (
              <CertificatesOverview certificates={getSkillCertifications(skillDetails.name)} />
            )}
          </motion.div>
        </motion.div>
      </main>
    </TooltipProvider>
  );
}

/**
 * Companies Overview Component
 * 
 * Displays a grid of companies where the skill was used, showing:
 * - Company logo and name
 * - Number of roles/activities at each company
 * - Total experience duration in months
 * - Skill points ratio for each company
 * - Number of projects using the skill
 * 
 * Features:
 * - Responsive grid layout (1-3 columns based on screen size)
 * - Hover effects with shadow transitions
 * - Staggered animations for visual appeal
 * - Avatar fallback for missing logos
 * - Clickable skill points with calculation modal
 * 
 * @param companies - Array of SkillCompany objects containing aggregated company data
 * @param skillDetails - Skill details data for calculation modal
 * @param professionalSkills - Professional skills data for calculation modal
 * @param activities - Activities data for company-specific calculations
 * @returns JSX.Element - Grid of company cards with statistics
 */
const CompaniesOverview: React.FC<{ 
  companies: SkillCompany[], 
  skillDetails: SkillDetailsType,
  professionalSkills: any,
  activities: any[]
}> = ({ companies, skillDetails, professionalSkills, activities }) => {
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold">Companies where {companies.length > 0 ? 'this skill was used' : 'skill was used'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies.map((company, index) => (
          <motion.div
            key={company.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: createStaggeredDelay(index) }}
          >
            <Link href={`/professional/${createCompanySlug(company.name)}`}>
              <Card className="h-full hover:shadow-md transition-shadow cursor-pointer">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      <AvatarImage 
                        src={company.logoPath} 
                        alt={company.name}
                        className="object-contain"
                      />
                      <AvatarFallback>{company.logoText}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h4 className="font-medium hover:text-primary transition-colors">{company.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {company.activitiesCount} {company.activitiesCount === 1 ? 'role' : 'roles'}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Experience:</span>
                      <span className="font-medium">
                        {Math.round(company.totalDuration)} months
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Projects:</span>
                      <span className="font-medium">{company.projectsCount}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Skill Points:</span>
                      <SkillPointsRatio 
                        skillSpecificPoints={company.skillSpecificPoints}
                        totalSkillPoints={company.totalSkillPoints}
                        skillName={skillDetails.name}
                        category={skillDetails.category}
                        calculationType="company"
                        skillsByCategory={professionalSkills}
                        activities={activities.filter(activity => activity.companyId === company.id)}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/**
 * Skill Timeline View Component
 * 
 * Displays a chronological timeline of skill development across activities, showing:
 * - Activity titles and company associations
 * - Start and end dates (with "Present" for ongoing activities)
 * - Number of projects using the skill in each activity
 * - Skill points ratio for each timeline entry
 * 
 * Features:
 * - Chronological ordering (most recent first)
 * - Visual indicators for ongoing activities
 * - Company logos with fallback avatars
 * - Responsive card layout with animations
 * - Date formatting for human readability
 * - Clickable skill points with calculation modal
 * 
 * @param timeline - Array of SkillTimeline objects representing skill usage over time
 * @param skillDetails - Skill details data for calculation modal
 * @param professionalSkills - Professional skills data for calculation modal
 * @returns JSX.Element - Timeline cards showing skill development progression
 */
const SkillTimelineView: React.FC<{ 
  timeline: SkillTimeline[], 
  skillDetails: SkillDetailsType,
  professionalSkills: any,
  activities: any[]
}> = ({ timeline, skillDetails, professionalSkills, activities }) => {
  const formatDate = (date: string | null): string => {
    if (!date || date === 'Present') return 'Present';
    
    const parsedDate = new Date(date);
    return parsedDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold">Skill Development Timeline</h3>
      <div className="space-y-4">
        {timeline.map((item, index) => (
          <motion.div
            key={`${item.activityTitle}-${item.startDate}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: createStaggeredDelay(index) }}
          >
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Link href={`/activities/${createActivitySlug(item.activityTitle)}`}>
                        <h4 className="font-medium hover:text-primary transition-colors cursor-pointer">
                          {item.activityTitle}
                        </h4>
                      </Link>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {formatDate(item.startDate)} - {formatDate(item.endDate)}
                        {item.isOngoing && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Ongoing
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {item.companyName}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {item.projectsCount} {item.projectsCount === 1 ? 'project' : 'projects'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2 text-sm">
                      <span className="text-muted-foreground">Skill Points:</span>
                      <SkillPointsRatio 
                        skillSpecificPoints={item.skillSpecificPoints}
                        totalSkillPoints={item.totalSkillPoints}  // Use activity-specific total, not global total
                        skillName={skillDetails.name}
                        category={skillDetails.category}
                        calculationType="activity"
                        skillsByCategory={professionalSkills}
                        activityId={activities.find(activity => activity.title === item.activityTitle)?.id}
                        activities={activities}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/**
 * Projects Overview Component
 * 
 * Displays a grid of all projects that utilized the skill, showing:
 * - Project title and description
 * - Associated activity and company information
 * - Project duration and dates
 * - Skill weight/proficiency percentage for this project
 * - Company logo with fallback avatar
 * - Multi-activity project indicators and navigation
 * 
 * Features:
 * - Responsive grid layout (1-2 columns based on screen size)
 * - Hover effects and transitions
 * - Staggered animations for visual appeal
 * - Skill points ratio display
 * - Date range formatting
 * - Continued project badges and navigation links
 * - Clickable project cards (if navigation is needed in the future)
 * 
 * @param projects - Array of SkillProject objects containing project details
 * @param skillDetails - Skill details for calculation modal
 * @param professionalSkills - Professional skills data for calculation modal
 * @returns JSX.Element - Grid of project cards with skill-specific information
 */
const ProjectsOverview: React.FC<{ 
  projects: SkillProject[];
  skillDetails: SkillDetailsType;
  professionalSkills: any;
}> = ({ projects, skillDetails, professionalSkills }) => {
  const formatDate = (date: string | null): string => {
    if (!date || date === 'Present') return 'Present';
    
    const parsedDate = new Date(date);
    return parsedDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold">Projects using this skill</h3>
      <div className="space-y-4">
        {projects.map((project, index) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: createStaggeredDelay(index) }}
          >
            <Card className={`p-4 hover:bg-muted/50 transition-all duration-300 ${
              project.isMultiActivity 
                ? 'bg-background/60 border-primary/30' 
                : 'bg-background'
            }`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Link href={`/project/${project.title.toLowerCase().replace(/ /g, '-')}`}>
                        <h4 className={`text-lg font-semibold truncate cursor-pointer hover:text-primary transition-colors ${project.isMultiActivity ? 'text-foreground/80' : ''}`}>
                          {project.title}
                        </h4>
                      </Link>
                      {project.isMultiActivity && (
                        <span className="bg-primary/20 text-primary text-xs px-2 py-1 rounded-full whitespace-nowrap">
                          Multi-Activity
                        </span>
                      )}
                    </div>
                    <div className="flex justify-end mb-2">
                      <SkillPointsRatio 
                        skillSpecificPoints={project.skillSpecificPoints}
                        totalSkillPoints={project.totalSkillPoints}
                        skillName={skillDetails.name}
                        category={skillDetails.category}
                        calculationType="project"
                        skillsByCategory={professionalSkills}
                        projectTitle={project.title}
                        className="scale-75"
                      />
                    </div>                    {project.isMultiActivity ? (
                      <div className="space-y-1 mb-3">
                        {project.nextActivityTitle && (
                          <div className="text-xs text-muted-foreground/80">
                            Continued in: <Link href={`/activities/${createActivitySlug(project.nextActivityTitle)}`}>
                              <span className="text-primary hover:underline cursor-pointer">
                                {project.nextActivityTitle}
                              </span>
                            </Link>
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground/80">
                          Started in: <Link href={`/activities/${createActivitySlug(project.activityTitle)}`}>
                            <span className="text-primary hover:underline cursor-pointer">
                              {project.activityTitle}
                            </span>
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground mb-3">
                        <Link href={`/activities/${createActivitySlug(project.activityTitle)}`}>
                          <span className="text-primary hover:underline cursor-pointer">
                            {project.activityTitle}
                          </span>
                        </Link> @ {project.companyName}
                      </p>
                    )}
                    
                    <Link href={`/project/${project.title.toLowerCase().replace(/ /g, '-')}`}>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-3 cursor-pointer hover:text-foreground transition-colors">
                        {project.description}
                      </p>
                    </Link>
                  </div>
                  
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                      <Calendar className="h-4 w-4" />
                      {formatDate(project.startDate)} - {formatDate(project.endDate)}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      {project.duration} {project.duration === 1 ? 'month' : 'months'}
                      {(!project.endDate || project.endDate === 'Present') && (
                        <span className="ml-1 text-primary">(ongoing)</span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/**
 * Certificates Overview Component
 * 
 * Displays certificates that are related to the current skill, showing:
 * - Certificate logo/badge
 * - Certificate title and issuing organization
 * - Issue and expiry dates
 * - Direct link to certificate credential
 * - Download link for certificate PDF
 * 
 * Features:
 * - Responsive grid layout
 * - Click-to-view certificate credentials
 * - Proper date formatting
 * - External link icons and indicators
 * 
 * @param certificates - Array of certificate objects that cover the current skill
 */
interface CertificatesOverviewProps {
  certificates: Array<{
    id: number;
    title: string;
    issuingOrganization: string;
    issueDate: string;
    expiryDate: string;
    pdfPath: string;
    skills: string[];
    logoPath: string;
    link: string;
    displayOrder: number;
    isVisible: boolean;
  }>;
}

const CertificatesOverview: React.FC<CertificatesOverviewProps> = ({ certificates }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long',
      day: 'numeric'
    });
  };

  if (certificates.length === 0) {
    return (
      <div className="text-center py-12">
        <Award className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold mb-2">No Certificates Found</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          This skill is not currently covered by any certifications in the database.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        {certificates
          .sort((a, b) => new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime())
          .map((cert, index) => (
          <motion.div
            key={cert.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
          >
            <Link href={`/certificate/${cert.id}`}>
              <Card className="h-full hover:shadow-lg transition-shadow duration-300 cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Certificate Logo */}
                    <div className="flex-shrink-0">
                      <img 
                        src={cert.logoPath} 
                        alt={`${cert.title} certification badge`}
                        className="w-20 h-20 object-contain rounded-lg p-1"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center hidden"
                      >
                        <Award className="h-10 w-10 text-white" />
                      </div>
                    </div>
                    
                    {/* Certificate Details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-lg mb-1 line-clamp-2">
                        {cert.title}
                      </h4>
                      <p className="text-muted-foreground mb-3">
                        {cert.issuingOrganization}
                      </p>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>
                            Issued: {formatDate(cert.issueDate)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>
                            Expires: {formatDate(cert.expiryDate)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
